/**
 * ActionCardPure
 *
 * Pure presentational component for action cards in the one-off action section.
 * No server dependencies - all data and callbacks passed via props.
 */
import type { ReactNode } from 'react';

import { Button, ButtonGroup, ButtonA } from '../Input2';
import { StepIconSvg } from '../StepIcon';
import { Editor, Media, Communication } from '../StrokeIcons';
import { Tooltip } from '../Tooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * Icon types for user-facing actions.
 * Excludes cadence-internal step types (delay, wait_for_connect, sift)
 * that don't represent manual user actions.
 */
export type ActionCardIconType =
  | 'message'
  | 'connect'
  | 'email'
  | 'inmail'
  | 'follow'
  | 'reminder'
  | 'voice_note'
  | 'call'
  | 'visit'
  | 'likeRecentPosts';

export type ActionCardPureProps = {
  /** Label to display on the button */
  label: ReactNode;
  /** Icon type for the step icon */
  iconType: ActionCardIconType;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Error message to show as tooltip when disabled */
  error?: string;
  /** Phone number for call/SMS actions */
  tel?: string;
  /** Click handler for the main action */
  onClick?: () => void;
  /** Click handler for AI generate button */
  onClickAiGenerate?: () => void;
  /** Callback when manual call is recorded (for tel: links) */
  onRecordManualCall?: () => void;
  /** Whether to show the facetime call button (macOS only) */
  showFacetimeButton?: boolean;
  /**
   * Injected in-app click-to-call trigger (e.g. the dashboard's ClickToCallButton
   * or the extension's softphone trigger). Kept as an opaque slot so this pure
   * component stays free of tRPC/platform dependencies while both surfaces share it.
   */
  clickToCall?: ReactNode;
};

// ============================================================================
// Component
// ============================================================================

export function ActionCardPure({
  label,
  iconType,
  disabled,
  error,
  tel,
  onClick,
  onClickAiGenerate,
  onRecordManualCall,
  showFacetimeButton,
  clickToCall,
}: ActionCardPureProps) {
  return (
    <Tooltip label={disabled && error ? error : undefined}>
      <div className="flex gap-1.5">
        <ButtonGroup className="flex-1" color="blue" tone="soft">
          <Button
            className="flex-1"
            onClick={onClick}
            disabled={disabled}
            icon={<StepIconSvg iconType={iconType} />}>
            {label}
          </Button>
          {onClickAiGenerate && !disabled && (
            <Button
              tooltip="Generate with AI"
              onClick={onClickAiGenerate}
              icon={<Editor.Wand />}
            />
          )}
        </ButtonGroup>
        {clickToCall}
        {tel && showFacetimeButton && (
          <ButtonA
            color="blue" tone="soft"
            bordered={false}
            tooltip="Call with your phone (facetime)"
            disabled={disabled}
            href={`tel:${tel}`}
            target="_blank"
            onClick={() => onRecordManualCall?.()}
            icon={<Media.Phone01 />}
          />
        )}
        {tel && (
          <ButtonA
            color="blue" tone="soft"
            bordered={false}
            tooltip="Send SMS"
            href={`sms:${tel}`}
            disabled={disabled}
            target="_blank"
            icon={<Communication.MessageTextSquare02 />}
          />
        )}
      </div>
    </Tooltip>
  );
}
